import type { SchemaBuilderColumn, SchemaColumnResponse } from "../types";

/** Attach database IDs returned after creating a table or adding a column. */
export function withPersistedColumnIds(
  columns: SchemaBuilderColumn[],
  persistedColumns: SchemaColumnResponse[],
): SchemaBuilderColumn[] {
  const idsByName = new Map(
    persistedColumns.map((column) => [column.column_name, column.id]),
  );

  return columns.map((column) => ({
    ...column,
    id: idsByName.get(column.name) ?? column.id,
  }));
}

/** Return only non-primary-key columns that have not been saved yet. */
export function columnsToAdd(
  columns: SchemaBuilderColumn[],
): SchemaBuilderColumn[] {
  return columns.filter((column) => !column.id && !column.isPrimaryKey);
}

/** Delete a field remotely before removing its local builder state. */
export async function deleteBuilderColumn(
  tableId: string,
  column: SchemaBuilderColumn,
  dropColumn: (columnId: string) => Promise<void>,
  removeColumn: (tableId: string, tempColumnId: string) => void,
  onError: (message: string) => void,
  inFlightColumnIds: Set<string> = new Set(),
  onPendingChange: (tempColumnId: string, pending: boolean) => void = () => {},
): Promise<void> {
  if (inFlightColumnIds.has(column.tempId)) return;

  inFlightColumnIds.add(column.tempId);
  onPendingChange(column.tempId, true);
  try {
    if (column.id) {
      await dropColumn(column.id);
    }
  } catch (error) {
    onError(error instanceof Error ? error.message : "Failed to drop column");
    return;
  } finally {
    inFlightColumnIds.delete(column.tempId);
    onPendingChange(column.tempId, false);
  }

  removeColumn(tableId, column.tempId);
}
